import {
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	IconButton,
	Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { GridColDef } from '@mui/x-data-grid';
import { selectMyInfo } from '@/pages/auth/authSlice';
import { PermissionEnum } from '@/api/enum/common.enum';
import LocalDataGrid from '@/common/components/dataGrid/LocalDataGrid';
import { FamilyMemberDuplicationCheckDTO } from '@/api/DTO/family/family.interface';
import {
	MatchedNameCell,
	MatchedText,
} from '@/common/components/member-duplication/DuplicationCells';

interface Props {
	open: boolean;
	rows: FamilyMemberDuplicationCheckDTO[];
	/** `true` = staff confirmed "not the same person"; `false` = cancelled. */
	onClose: (confirmed: boolean) => void;
}

/** Columns shared by the in-progress (CHA-003) and closed (CHA-004) family member hits. */
export const useFamilyDuplicationColumns =
	(): GridColDef<FamilyMemberDuplicationCheckDTO>[] => {
		const { t } = useTranslation();
		const userInfo = useSelector(selectMyInfo);
		const canView = userInfo?.permissionList?.includes(
			PermissionEnum.ViewFamilyMember
		);
		return [
			{
				field: 'familyMemberFileNo',
				headerName: t('family.familyMemberFileNo'),
				flex: 18,
			},
			{
				field: 'nameEn',
				headerName: t('family.name'),
				flex: 20,
				renderCell: (params) => (
					<MatchedNameCell
						nameEn={params.row.nameEn}
						nameZh={params.row.nameZh}
						matchedFields={params.row.matchedFields}
					/>
				),
			},
			{
				field: 'phone',
				headerName: t('family.mobilePhone'),
				flex: 14,
				renderCell: (params) => (
					<MatchedText
						value={params.row.phone}
						field='PHONE'
						matchedFields={params.row.matchedFields}
					/>
				),
			},
			{
				field: 'hkid',
				headerName: t('member.HKID'),
				flex: 14,
				renderCell: (params) => (
					<MatchedText
						value={params.row.hkid}
						field='HKID'
						matchedFields={params.row.matchedFields}
					/>
				),
			},
			{
				field: 'view',
				headerName: '',
				width: 68,
				renderCell: (params) =>
					canView && (
						<a
							href={`/family-management/family-management-list/detail/${params.row.id}`}
							target='_blank'
							rel='noopener noreferrer'
						>
							<IconButton color='secondary'>
								<VisibilityOutlinedIcon />
							</IconButton>
						</a>
					),
			},
		];
	};

/**
 * DMS-REQ-WEB-CHA-003 §四: admitted family members of this centre that may be
 * the same person. Staff must confirm the applicant is not the same person.
 */
export default function FamilyMemberDuplicateDialog({
	open,
	rows,
	onClose,
}: Props) {
	const { t } = useTranslation();
	const [confirmed, setConfirmed] = useState(false);
	const columns = useFamilyDuplicationColumns();

	useEffect(() => {
		if (open) setConfirmed(false);
	}, [open]);

	return (
		<Dialog open={open} onClose={() => onClose(false)} maxWidth='lg' fullWidth>
			<DialogTitle variant='h5'>
				{t('memberDuplication.inProgressTitle')}
			</DialogTitle>
			<IconButton
				aria-label='close'
				onClick={() => onClose(false)}
				sx={{
					position: 'absolute',
					right: 8,
					top: 8,
					color: (theme) => theme.palette.grey[500],
				}}
			>
				<CloseIcon />
			</IconButton>
			<DialogContent>
				<Typography variant='body2' sx={{ marginBottom: '16px' }}>
					{t('memberDuplication.familyInProgressText')}
				</Typography>
				<LocalDataGrid
					rows={rows}
					columns={columns}
					getRowId={(row) => row.id}
					pagination={false}
				/>
				<FormControlLabel
					sx={{ marginTop: '16px' }}
					control={
						<Checkbox
							checked={confirmed}
							onChange={(e) => setConfirmed(e.target.checked)}
						/>
					}
					label={t('memberDuplication.notSamePersonConfirm')}
				/>
			</DialogContent>
			<DialogActions>
				<Button sx={{ textTransform: 'none' }} onClick={() => onClose(false)}>
					{t('button.cancel')}
				</Button>
				<Button
					sx={{ textTransform: 'none' }}
					variant='contained'
					disabled={!confirmed}
					onClick={() => onClose(true)}
				>
					{t('button.confirm')}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
